import { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    createFieldErrorStructure,
    deriveBirthdayFromAge,
    formatDateISO,
    getEmptyRequiredFields,
    hasActiveErrors,
    setErrorMessage,
} from '../helpers/toolsHelper';
import { AGE_LIMITS, ERROR_TYPES } from '../constants/validationConstants';
import { TextsContext } from './textsContext';
import { useAuth } from './userContext';
import {
    CHANGE_DATA,
    CHANGE_BENEFIT,
    CHANGE_BENEFIT_NAME,
    CHANGE_BENEFIT_ID,
    REMOVE_BENEFITS,
    ADD_CALCULATION,
    SET_CALCULATION_ERROR,
    POPULATE_CONTEXT,
    createQuoteFormInitialState,
    quoteFormReducer,
} from './quoteFormState';
import { useFavoritePlan } from '../hooks/myFavoritePlan/useFavoritePlan';
import { getPlanFormState } from '../helpers/FavoritePlanToQuote';
import { isFavoritePlanStale } from '../helpers/FavoritePlanValidation';
import { useCoverageSelection } from '../hooks/useCoverageSelection';
import { useQuoter } from '../api/quoterApi';
import { resolveCountryResidenceDeductibleFlag } from '../helpers/DeductibleRules';
import { GET_COUNTRY_WITH_CHANGE_DEDUCTIBLE_LIST } from '../graphql/queries';
import { MY_FAVORITE_PLAN_STEPS, findFavPlanStepIndex, FAV_PLAN_FIRST_STEP_SLUG } from '../constants/myFavoritePlanForm';
import {
    getCoverageOptionsRequiredFields,
    requiresLimitedCoverAcceptance,
    getPlanBenefitsRequiredFields,
    getOutpatientBenefitsRequiredFields,
    getMaternityRequiredFields,
    getPreventiveCheckupRequiredFields,
    getEyeSurgeryRequiredFields,
} from '../helpers/StepAdvanceValidation';

const MyFavoritePlanFormContext = createContext(null);

const BASE_INFORMATION_REQUIRED_FIELDS = ['gender', 'age', 'country'];

const DEFAULT_COVERAGE_LEAD_DAYS = 15;

// Fallback beneficiary identity for the pricing request when the logged-in
// user's name isn't available (e.g. the SSO/agent-portal login path never
// populates user.name - only the username/password login flow does).
const DEFAULT_BENEFICIARY_NAME = 'Favorite Plan';
const DEFAULT_BENEFICIARY_SURNAME = 'Template';
const DEFAULT_BENEFICIARY_EMAIL = 'favorite-plan-template@vumi.com';

const initialState = createQuoteFormInitialState({
    age: '',
});

export const MyFavoritePlanFormProvider = ({ children }) => {
    const { texts } = useContext(TextsContext);
    const { user } = useAuth();
    const navigate = useNavigate();
    // Con `id` en la URL (/fav-plan/:id/...) el formulario edita un plan existente.
    const { id: favoritePlanId } = useParams();
    const isEditMode = Boolean(favoritePlanId);
    const [favoritePlanFormState, dispatch] = useReducer(quoteFormReducer, initialState);
    const [errors, setErrors] = useState({});
    const [pendingPath, setPendingPath] = useState(null);
    // Estado contra el que se comparan los cambios: el inicial al crear, o el plan
    // recién cargado al editar (si no, editar mostraría siempre "cambios sin guardar").
    const baselineStateRef = useRef(initialState);
    // Evita re-hidratar si react-query vuelve a emitir el mismo plan (refetch, foco…),
    // que pisaría lo que el usuario ya editó.
    const hasHydratedRef = useRef(false);

    const changeDataValue = (name, value) => {
        dispatch({ type: CHANGE_DATA, payload: { name, value } });
    };

    const handleOnChange = (e, meta = null) => {
        const name = e?.target?.name ?? meta?.name;
        const value = e?.target?.value ?? e?.value;
        changeDataValue(name, value);
    };

    const handleChecks = (e) => {
        handleOnChange({ target: { value: e.target.checked, name: e.target.name } });
    };

    const changeBenefitValue = (name, value) => {
        dispatch({ type: CHANGE_BENEFIT, payload: { name, value } });
    };

    const changeBenefitNameValue = (name, value) => {
        dispatch({ type: CHANGE_BENEFIT_NAME, payload: { name, value } });
    };

    const changeBenefitIdValue = (name, value) => {
        dispatch({ type: CHANGE_BENEFIT_ID, payload: { name, value } });
    };

    const removeBenefits = (name) => {
        dispatch({ type: REMOVE_BENEFITS, payload: { name } });
    };

    const addCalculation = (payload) => {
        dispatch({ type: ADD_CALCULATION, payload });
    };

    const addCalculationError = () => {
        dispatch({ type: SET_CALCULATION_ERROR });
    };

    // Hidrata el formulario con un plan guardado. POPULATE_CONTEXT hace merge sobre
    // el estado inicial (no lo reemplaza), así que un `data` viejo al que le falten
    // campos conserva sus defaults en vez de quedar en undefined.
    const populateContext = (payload) => {
        dispatch({ type: POPULATE_CONTEXT, payload });
    };

    const {
        benefits,
        ignoredBenefits,
        setIgnoredBenefits,
        preferreds,
        getAllBenefits,
        handleOnChangeBenefit,
        calculateSelectedItem,
    } = useCoverageSelection({
        selectedBenefits: favoritePlanFormState.selectedBenefits,
        selectedBenefitsIds: favoritePlanFormState.selectedBenefitsIds,
        selectedBenefitsNames: favoritePlanFormState.selectedBenefitsNames,
        dependants: favoritePlanFormState.dependants,
        addDependant: favoritePlanFormState.addDependant,
        country: favoritePlanFormState.country,
        isCountryResidenceDeductible: favoritePlanFormState.isCountryResidenceDeductible,
        hasLoadedQuoteContext: favoritePlanFormState.hasLoadedQuoteContext,
        isCountryResidenceDeductibleFlagPresent: favoritePlanFormState.isCountryResidenceDeductibleFlagPresent,
        changeBenefitValue,
        changeBenefitNameValue,
        changeBenefitIdValue,
    });

    // Sólo para leer el catálogo del rider de órganos (mismo queryKey 'rider' que ya usa
    // ApiContext/OrganOptions vía react-query, así que no duplica la llamada de red) y
    // cruzarlo contra un plan guardado al hidratar (ver más abajo).
    const { organRiderQuery } = useQuoter();

    // Mirrors Step1Pages' country -> isCountryResidenceDeductible resolution so the
    // shared coverage steps see the same deductible rules regardless of which form
    // is driving them.
    const { data: deductibleCountriesData } = useQuery(GET_COUNTRY_WITH_CHANGE_DEDUCTIBLE_LIST);

    const getCountryDeductibleFlagValue = useCallback((countryValue) => {
        return resolveCountryResidenceDeductibleFlag({
            countryValue,
            deductibleCountries: deductibleCountriesData?.countriesWithDeductibleByCountryOfResidence,
            contextState: favoritePlanFormState,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deductibleCountriesData, favoritePlanFormState]);

    useEffect(() => {
        const nextValue = getCountryDeductibleFlagValue(favoritePlanFormState.country);

        if (favoritePlanFormState.isCountryResidenceDeductible !== nextValue) {
            changeDataValue('isCountryResidenceDeductible', nextValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoritePlanFormState.country, deductibleCountriesData]);

    // Modo edición: trae el plan y lo vuelca en el formulario una sola vez. Dentro del
    // payload de pricing guardado, `contextData` es un snapshot de este mismo estado e
    // incluye `calculation`, así que el costo estimado aparece sin recalcular.
    const {
        data: favoritePlanResponse,
        isLoading: isLoadingFavoritePlan,
        isError: isFavoritePlanError,
    } = useFavoritePlan(favoritePlanId);

    // El plan guardado ya no es cotizable tal cual está (revalidado por compute, ver
    // helpers/FavoritePlanValidation.js). `false` => sin aviso pendiente. Es sólo
    // informativo: a partir de un 400 genérico no hay forma de saber qué campo
    // puntual quedó inválido, así que el estado se hidrata tal cual se guardó.
    const [staleBenefitWarning, setStaleBenefitWarning] = useState(false);
    const dismissStaleBenefitWarning = () => setStaleBenefitWarning(false);

    const isBenefitsCatalogLoading = benefits.regular.isLoading || benefits.structural.isLoading || organRiderQuery.isLoading;

    useEffect(() => {
        const plan = favoritePlanResponse?.data;
        if (!plan?.data || hasHydratedRef.current) return;
        // Sigue esperando al catálogo antes de hidratar (useCoverageSelection lo
        // necesita para resolver ignored/selected), aunque ya no se use para cruzarlo
        // contra el plan guardado - eso ahora lo valida el compute, no el front.
        if (isBenefitsCatalogLoading) return;

        const formState = getPlanFormState(plan.data);

        hasHydratedRef.current = true;
        populateContext(formState);
        // El plan cargado pasa a ser la referencia de "sin cambios". Tiene que salir del
        // mismo objeto que se acaba de volcar: si apuntara a otro, el form se marcaría
        // como sucio nada más abrirlo.
        baselineStateRef.current = { ...initialState, ...formState };

        isFavoritePlanStale(plan.data).then((isStale) => {
            if (isStale) setStaleBenefitWarning(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoritePlanResponse, isBenefitsCatalogLoading]);

    // The pricing request needs a real coverage start date, but a template isn't
    // tied to one - default it once so it's never blank when a quote is computed.
    useEffect(() => {
        if (favoritePlanFormState.coverage) return;

        const defaultCoverage = new Date();
        defaultCoverage.setDate(defaultCoverage.getDate() + DEFAULT_COVERAGE_LEAD_DAYS);
        changeDataValue('coverage', formatDateISO(defaultCoverage));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Base Information collects a plain age instead of a birthdate; the pricing
    // request (and maternity-rider eligibility) need a real dateOfBirth, so keep
    // one in sync with whatever age is currently entered.
    useEffect(() => {
        const nextBirthday = deriveBirthdayFromAge(favoritePlanFormState.age);

        if (nextBirthday && favoritePlanFormState.birthday !== nextBirthday) {
            changeDataValue('birthday', nextBirthday);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoritePlanFormState.age]);

    // The pricing request's beneficiary fields don't affect price, but the
    // backend schema expects them populated - use the logged-in agent's name
    // when available (only the username/password login flow sets user.name;
    // the SSO/agent-portal path never does), falling back to a generic label.
    useEffect(() => {
        const nameParts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);

        changeDataValue('name', nameParts[0] || DEFAULT_BENEFICIARY_NAME);
        changeDataValue('surname', nameParts.slice(1).join(' ') || DEFAULT_BENEFICIARY_SURNAME);
        changeDataValue('email', DEFAULT_BENEFICIARY_EMAIL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Estado de los campos obligatorios de Base Information, sin tocar `errors`.
    // Lo comparten el gate del botón Next (validateBaseInformation) y la marca de
    // pendientes del sidebar (isStepComplete), que se evalúa en render y por eso
    // no puede escribir estado.
    const getBaseInformationIssues = () => {
        const emptyFields = getEmptyRequiredFields(favoritePlanFormState, BASE_INFORMATION_REQUIRED_FIELDS);
        const age = Number(favoritePlanFormState.age);

        return {
            emptyFields,
            isAgeInRange: age >= AGE_LIMITS.MIN_AGE && age <= AGE_LIMITS.MAX_AGE,
        };
    };

    const validateBaseInformation = () => {
        const requiredFieldMessage = texts?.general_field_validation_required ?? 'This field is required';
        const ageRangeMessage = texts?.favorite_plan_form_field_age_range_error
            ?? `Age must be between ${AGE_LIMITS.MIN_AGE} and ${AGE_LIMITS.MAX_AGE} years inclusive.`;
        const validationErrors = {};

        const { emptyFields, isAgeInRange } = getBaseInformationIssues();

        BASE_INFORMATION_REQUIRED_FIELDS.forEach(fieldKey => {
            Object.assign(validationErrors, createFieldErrorStructure(errors, fieldKey));
            setErrorMessage(
                validationErrors[fieldKey],
                ERROR_TYPES.EMPTY,
                emptyFields.includes(fieldKey) ? requiredFieldMessage : null
            );
        });

        if (!emptyFields.includes('age')) {
            setErrorMessage(
                validationErrors.age,
                ERROR_TYPES.INVALID,
                isAgeInRange ? null : ageRangeMessage
            );
        }

        setErrors({ ...errors, ...validationErrors });

        return hasActiveErrors(validationErrors);
    };

    // Mandatory-field gate for the shared steps (Coverage Options through Riders),
    // mirroring the quoter's own per-step required-field checks (useSteps.jsx's
    // isStepComplete/benefitsGenerator, quoter-only). Deductible Reduction and
    // Summary have nothing to check here (see helpers/StepAdvanceValidation.js
    // and the plan for why).
    //
    // Declarativo y sin efectos: lo consume `validateStep` (que además escribe los
    // errores del paso que se está dejando) y `isStepComplete` (que sólo pregunta).
    const getStepRequirements = (slug) => {
        const {
            selectedBenefitsIds, isCountryResidenceDeductible, addDependant, outpationBenefitsEnabled,
            outpationBenefitsmaternidad, step14Confirmation, limitedCover, organ, organRider,
            nonAmbulance, nonMaternidad,
        } = favoritePlanFormState;

        switch (slug) {
            case 'coverage-options':
                return {
                    requiredBenefitKeys: getCoverageOptionsRequiredFields(selectedBenefitsIds, isCountryResidenceDeductible),
                    extraFieldKey: 'limitedCover',
                    extraFieldInvalid: requiresLimitedCoverAcceptance(selectedBenefitsIds) && !limitedCover,
                };
            case 'plan-benefits':
                return {
                    requiredBenefitKeys: getPlanBenefitsRequiredFields(addDependant),
                    extraFieldKey: 'organ',
                    extraFieldInvalid: Boolean(organ) && organRider === 0,
                };
            case 'outpatient-benefits':
                return {
                    requiredBenefitKeys: getOutpatientBenefitsRequiredFields(outpationBenefitsEnabled),
                    extraFieldKey: 'nonAmbulance',
                    extraFieldInvalid: !outpationBenefitsEnabled && !nonAmbulance,
                };
            case 'riders-maternity':
                // Sin esto, un usuario que nunca puede llegar a este paso (no elegible)
                // mostraría el módulo "Riders" del sidebar como pendiente para siempre,
                // porque nunca podría marcar el disclaimer (`nonMaternidad`) de una
                // página a la que no tiene acceso. Mismo criterio de skip que usa el
                // cotizador para excluir su propio step de maternidad (isFemale()).
                if (!isFemale()) return null;
                return {
                    requiredBenefitKeys: getMaternityRequiredFields(outpationBenefitsmaternidad),
                    extraFieldKey: 'nonMaternidad',
                    extraFieldInvalid: !nonMaternidad,
                };
            case 'riders-preventive-checkup':
                return {
                    requiredBenefitKeys: getPreventiveCheckupRequiredFields(step14Confirmation),
                    extraFieldKey: null,
                    extraFieldInvalid: false,
                };
            case 'riders-eye-surgery':
                return {
                    requiredBenefitKeys: getEyeSurgeryRequiredFields(),
                    extraFieldKey: null,
                    extraFieldInvalid: false,
                };
            default:
                return null;
        }
    };

    const getMissingBenefits = (requiredBenefitKeys = []) => requiredBenefitKeys.filter(
        key => !Object.keys(favoritePlanFormState.selectedBenefitsIds).includes(key)
    );

    // `errors` sólo se llena al presionar Next (validateBaseInformation/validateStep);
    // sin este efecto, corregir un campo no borra su error hasta volver a presionar Next.
    // Sólo limpia errores ya resueltos - nunca marca uno nuevo - así el mensaje desaparece
    // en cuanto el campo queda válido, sin esperar a un nuevo intento de avanzar.
    useEffect(() => {
        if (!hasActiveErrors(errors)) return;

        const nextErrors = { ...errors };
        let changed = false;

        const clearIfResolved = (container, fieldKey, errorType, isResolved) => {
            if (!isResolved || !container[fieldKey]?.[errorType]) return;
            container[fieldKey] = { ...container[fieldKey], [errorType]: null };
            changed = true;
        };

        const { emptyFields, isAgeInRange } = getBaseInformationIssues();
        BASE_INFORMATION_REQUIRED_FIELDS.forEach(fieldKey => {
            clearIfResolved(nextErrors, fieldKey, ERROR_TYPES.EMPTY, !emptyFields.includes(fieldKey));
        });
        clearIfResolved(nextErrors, 'age', ERROR_TYPES.INVALID, isAgeInRange);

        if (errors.benefitsList) {
            nextErrors.benefitsList = { ...errors.benefitsList };
        }

        MY_FAVORITE_PLAN_STEPS.forEach(step => {
            const requirements = getStepRequirements(step.slug);
            if (!requirements) return;

            const { requiredBenefitKeys, extraFieldKey, extraFieldInvalid } = requirements;

            if (nextErrors.benefitsList) {
                const missingBenefits = getMissingBenefits(requiredBenefitKeys);
                requiredBenefitKeys.forEach(benefitKey => {
                    clearIfResolved(nextErrors.benefitsList, benefitKey, ERROR_TYPES.BLANK, !missingBenefits.includes(benefitKey));
                });
            }

            if (extraFieldKey) {
                clearIfResolved(nextErrors, extraFieldKey, ERROR_TYPES.INVALID, !extraFieldInvalid);
            }
        });

        if (changed) setErrors(nextErrors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoritePlanFormState]);

    const validateStep = (pathname) => {
        const slug = MY_FAVORITE_PLAN_STEPS[findFavPlanStepIndex(pathname)]?.slug;
        const requiredFieldMessage = texts?.general_field_validation_required ?? 'This field is required';
        const requirements = getStepRequirements(slug);

        if (!requirements) return false;

        const { requiredBenefitKeys, extraFieldKey, extraFieldInvalid } = requirements;
        const missingBenefits = getMissingBenefits(requiredBenefitKeys);

        const validationErrors = { benefitsList: { ...(errors.benefitsList ?? {}) } };

        requiredBenefitKeys.forEach(benefitKey => {
            const existingFieldError = errors?.benefitsList?.[benefitKey];
            validationErrors.benefitsList[benefitKey] = existingFieldError ?? {};
            setErrorMessage(
                validationErrors.benefitsList[benefitKey],
                ERROR_TYPES.BLANK,
                missingBenefits.includes(benefitKey) ? requiredFieldMessage : null
            );
        });

        if (extraFieldKey) {
            Object.assign(validationErrors, createFieldErrorStructure(errors, extraFieldKey));
            setErrorMessage(
                validationErrors[extraFieldKey],
                ERROR_TYPES.INVALID,
                extraFieldInvalid ? requiredFieldMessage : null
            );
        }

        setErrors({ ...errors, ...validationErrors });

        return missingBenefits.length > 0 || extraFieldInvalid;
    };

    /**
     * ¿El paso tiene resueltos sus campos obligatorios? Misma condición con la que
     * `validateStep` (y `validateBaseInformation` en el primero) bloquea el botón
     * Next, pero sin escribir en `errors`: el sidebar la consulta durante el render.
     * Deductible Reduction y Summary no tienen nada obligatorio que revisar.
     */
    const isStepComplete = (slug) => {
        if (slug === FAV_PLAN_FIRST_STEP_SLUG) {
            const { emptyFields, isAgeInRange } = getBaseInformationIssues();
            return emptyFields.length === 0 && isAgeInRange;
        }

        const requirements = getStepRequirements(slug);

        if (!requirements) return true;

        return getMissingBenefits(requirements.requiredBenefitKeys).length === 0 && !requirements.extraFieldInvalid;
    };

    /**
     * Módulos del sidebar con campos pendientes. Los riders comparten `id`, así que
     * basta con que uno de sus pasos esté incompleto para marcar el módulo.
     * Devuelve [] mientras se cargan el catálogo de beneficios o el plan que se está
     * editando: hasta que el estado se hidrata, todos los pasos parecerían vacíos.
     */
    const getIncompleteModules = () => {
        if (isBenefitsCatalogLoading || isLoadingFavoritePlan) return [];

        const incompleteModules = MY_FAVORITE_PLAN_STEPS
            .filter(step => !isStepComplete(step.slug))
            .map(step => step.id);

        return [...new Set(incompleteModules)];
    };

    const isFemale = () => {
        const age = Number(favoritePlanFormState.age);
        // eslint-disable-next-line eqeqeq
        return favoritePlanFormState.gender == 2 && age >= 18 && age <= 43;
    };

    // coverage/birthday/name/surname/email are synthesized automatically (see the
    // effects above) rather than entered by the user, so they'd make a freshly
    // opened, untouched form look "changed" and trip the unsaved-changes prompt.
    // birthday is redundant with age here anyway - age is what the user actually
    // edits, and it's still compared below.
    const stripAutoDerivedFields = ({ coverage, birthday, name, surname, email, ...rest }) => rest;

    const hasUnsavedChanges = () => {
        return JSON.stringify(stripAutoDerivedFields(favoritePlanFormState)) !== JSON.stringify(stripAutoDerivedFields(baselineStateRef.current));
    };

    const goToDashboard = () => {
        if (hasUnsavedChanges()) {
            setPendingPath('/dashboard');
            return;
        }
        navigate('/dashboard');
    };

    const confirmLeave = () => {
        if (pendingPath) {
            navigate(pendingPath);
        }
        setPendingPath(null);
    };

    const cancelLeave = () => {
        setPendingPath(null);
    };

    const value = {
        ...favoritePlanFormState,
        favoritePlanFormState,
        contextState: favoritePlanFormState,
        changeDataValue,
        handleOnChange,
        handleChecks,
        handleOnChangeBenefit,
        removeBenefits,
        addCalculation,
        addCalculationError,
        populateContext,
        // Modo edición
        favoritePlanId,
        isEditMode,
        isLoadingFavoritePlan,
        // Además del GET del plan, cubre el catálogo de beneficios/rider de órganos que
        // la hidratación espera para poder sanear ids obsoletos antes de volcar el
        // estado: evita que el form se vea un instante con los defaults antes de
        // hidratarse.
        isLoadingFavoritePlanForm: isLoadingFavoritePlan || (isEditMode && isBenefitsCatalogLoading),
        isFavoritePlanError,
        favoritePlanDescription: favoritePlanResponse?.data?.description,
        benefits,
        ignoredBenefits,
        setIgnoredBenefits,
        preferreds,
        getAllBenefits,
        calculateSelectedItem,
        staleBenefitWarning,
        dismissStaleBenefitWarning,
        isFemale,
        errors,
        setErrors,
        validateBaseInformation,
        validateStep,
        isStepComplete,
        getIncompleteModules,
        pendingPath,
        goToDashboard,
        confirmLeave,
        cancelLeave,
    };

    return (
        <MyFavoritePlanFormContext.Provider value={value}>
            {children}
        </MyFavoritePlanFormContext.Provider>
    );
};

export const useMyFavoritePlanForm = () => useContext(MyFavoritePlanFormContext);

export default MyFavoritePlanFormContext;
